import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import * as XLSX from "xlsx";
import DragDropUploader from "./components/DragDropUploader";
import HtmlTooltip from "./components/HtmlTooltip";
import ArrowDownIcon from "./components/icons/ArrowDownIcon";
import CheckCircleIcon from "./components/icons/CheckCircleIcon";
import CloseCircleIcon from "./components/icons/CloseCircleIcon";
import CsvIcon from "./components/icons/CsvIcon";
import DownloadIcon from "./components/icons/DownloadIcon";
import InfoCircleIcon from "./components/icons/InfoCircleIcon";
import RefreshIcon from "./components/icons/RefreshIcon";
import TrashIcon from "./components/icons/TrashIcon";
import Topbar from "./components/Topbar";

// Dynamic backend URL for local testing or production
const backendUrl = process.env.REACT_APP_API_URL;
console.log("Backend URL:", backendUrl);

const FileUpload = () => {
  // ✅ State for File Handling
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [listType, setListType] = useState("");
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const [cleanedFilePath, setCleanedFilePath] = useState("");
  const [uploadedFileSize, setUploadedFileSize] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCleaned, setIsCleaned] = useState(false);
  const [paid, setPaid] = useState(false);
  const [cleanedRowCount, setCleanedRowCount] = useState(null);
  const [removedRowCount, setRemovedRowCount] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  const mediaQuery = useMediaQuery("(min-width: 900px)");

  const storedUserId = localStorage.getItem("userId");

  if (!storedUserId) {
    console.error(
      "❌ User ID not found in localStorage. Make sure the user is logged in.",
    );
  } else {
    console.log("✅ Retrieved userId:", storedUserId);
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      // Count rows (excluding header)
      const rowCount = jsonData.length - 1;

      if (rowCount > 20000) {
        setErrorMessage(
          "❌ Error: File exceeds 20,000 rows. Please upload a smaller file.",
        );
      } else {
        setErrorMessage(""); // Clear error if valid
        setFile(file);
        console.log("✅ File is valid, proceeding with upload...");
        // You can call the upload function here
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // ✅ Handle Upload Success
  const handleUploadSuccess = (uploadedData) => {
    console.log("✅ File uploaded successfully:", uploadedData);

    setUploadedFilePath(uploadedData.file_url);
    setUploadedFileSize(uploadedData.size);

    // ✅ Ensure it captures the actual file name, fallback to "No file name available"
    const fileName = uploadedData.name || "No file name available";
    setUploadedFileName(fileName);
    setActiveStep(activeStep + 1);

    console.log("Updated uploadedFileName:", fileName); // Log the correct file name

    setPaid(false); // Reset payment state for new file
  };

  // Check for successful payment and trigger automatic download
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment") === "success") {
      setPaid(true);
      setMessage("✅ Payment confirmed! Your cleaned file is downloading...");

      const storedFilePath = localStorage.getItem("cleanedFilePath"); // ✅ Retrieve stored path
      console.log(
        "Debug: Retrieved cleanedFilePath after payment:",
        storedFilePath,
      );

      if (storedFilePath) {
        setCleanedFilePath(storedFilePath); // Restore state
        setTimeout(() => {
          handleDownload(storedFilePath);

          // ✅ Remove payment=success from URL after download
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);

          // ✅ Clear stored cleanedFilePath to prevent auto-download on refresh
          localStorage.removeItem("cleanedFilePath");
        }, 1000);
      } else {
        console.error("Error: No cleaned file path found after payment.");
        setMessage("❌ Error: No cleaned file found after payment.");
      }
    }
  }, []);

  // File Handlers
  const handleFileChange = (e) => {
    const newFile = e.target.files[0];
    if (!newFile) return;

    console.log("🔄 New file selected, resetting UI..."); // ✅ Debugging log

    setFile(newFile);
    setMessage(""); // ✅ Clear messages
    setUploadedFilePath(""); // ✅ Reset uploaded file path
    setCleanedFilePath(""); // ✅ Clear cleaned file path
    setCleanedRowCount(null); // ✅ Reset cleaned row count
    setRemovedRowCount(null); // ✅ Reset removed row count
    setIsCleaned(false); // ✅ Hide "Proceed to Payment" button
    setPaid(false); // ✅ Reset payment status
    setIsCleaning(false); // ✅ Ensure cleaning state is false
  };

  const handleListTypeChange = (e) => {
    setListType(e.target.value);
    e.target.value ? setActiveStep(1) : setActiveStep(0);
  };

  // File Upload Logic
  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage(""); // ✅ Clear any previous error message
    setPaid(false); // ✅ Reset payment state
    setCleanedFilePath(""); // ✅ Clear previous cleaned file

    if (!file) {
      setMessage("❌ Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("list_type", listType);

    try {
      const response = await axios.post(`${backendUrl}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200) {
        setUploadedFilePath(response.data.file_url);
        setMessage("✅ File uploaded successfully!"); // ✅ Success message clears errors
      }
    } catch (error) {
      console.error("Error during file upload: ", error);

      if (error.response && error.response.data) {
        if (error.response.data.detected_type) {
          const expectedList =
            listType === "property_radar" ? "Property Radar" : "PropStream";
          setMessage(`❌ Please upload a ${expectedList} list.`);
        } else {
          setMessage(`❌ ${error.response.data.message}`);
        }
      } else {
        setMessage("❌ Failed to upload file. Please try again.");
      }
    }
  };

  // Remove File Logic
  const handleRemoveFile = () => {
    setFile(null); 
    setUploadedFilePath("");
    setUploadedFileSize(0);
    setUploadedFileName("");
    setMessage("✅ File removed.");
  };
  

  // Clean File Logic
  const handleCleanFile = async () => {
    const storedUserId = localStorage.getItem("userId");

    if (!storedUserId) {
      console.error("❌ User ID not found in localStorage.");
      setMessage("❌ You must be logged in to clean files.");
      return;
    }

    console.log("📤 Sending userId to backend:", storedUserId); // Debugging line

    setIsCleaning(true); // ✅ Show cleaning message
    setMessage(""); // ✅ Clears "File uploaded successfully!" before cleaning starts

    try {
      const response = await axios.post(`${backendUrl}/clean`, {
        file_path: uploadedFilePath,
        list_type: listType,
        user_id: storedUserId, // ✅ Include user_id
      });

      if (response.status === 200) {
        setCleanedFilePath(response.data.cleaned_file_url);
        setCleanedRowCount(response.data.cleaned_row_count);
        setRemovedRowCount(response.data.removed_row_count);
        setIsCleaned(true);
        setMessage("✅ File cleaned successfully!");
      }
    } catch (error) {
      console.error("Error during file cleaning:", error);
      setMessage("❌ Failed to clean file. Please try again.");
    } finally {
      setIsCleaning(false); // ✅ Hide cleaning message when done
    }
  };

  // Download Logic
  const handleDownload = async (filePath) => {
    if (!filePath) {
      setMessage("❌ No file to download.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/download`, {
        params: { file_path: filePath },
      });

      if (response.status === 200 && response.data.download_url) {
        const link = document.createElement("a");
        link.href = response.data.download_url;
        link.download = "cleaned_and_removed_rows.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setMessage("✅ File downloaded successfully!");
      } else {
        setMessage("❌ Failed to generate download link.");
      }
    } catch (error) {
      console.error("Error during file download: ", error);
      setMessage("❌ Failed to download file.");
    } finally {
      setLoading(false);
    }
  };

  // Download Logic for Sample File
  const handleDownloadSample = async () => {
    if (!cleanedFilePath) {
      setMessage("❌ No sample file available.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/download`, {
        params: { file_path: cleanedFilePath, sample: "true" },
      });

      if (response.status === 200 && response.data.download_url) {
        const link = document.createElement("a");
        link.href = response.data.download_url;
        link.download = "cleaned_sample.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setMessage("✅ Sample file downloaded successfully!");
      } else {
        setMessage("❌ Failed to generate sample download link.");
      }
    } catch (error) {
      console.error("Error during sample file download: ", error);
      setMessage("❌ Failed to download sample file.");
    } finally {
      setLoading(false);
    }
  };

  // Stripe Payment Logic
  const handlePayment = async () => {
    setMessage(""); // Clear previous messages
    setLoading(true);

    if (!cleanedFilePath) {
      setMessage("❌ No cleaned file available.");
      setLoading(false);
      return;
    }

    try {
      localStorage.setItem("cleanedFilePath", cleanedFilePath); // ✅ Save file path

      const response = await axios.post(
        `${backendUrl}/create-checkout-session`,
        {
          amount: 1000, // Adjust price if needed
        },
      );

      if (response.data.url) {
        window.location.href = response.data.url; // Redirect user to Stripe Checkout
      } else {
        setMessage("❌ Failed to initiate checkout.");
      }
    } catch (error) {
      console.error("Error during checkout: ", error);
      setMessage("❌ Failed to start payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Logout Logic
  const handleLogout = () => {
    localStorage.removeItem("token"); // Remove authentication token
    localStorage.removeItem("cleanedFilePath"); // Ensure old file paths don’t persist
    window.location.href = "/login"; // Redirect to login page
  };

  return (
    <>
      <Topbar />
      <Box
        sx={{
          overflow: "hidden",
          pb: "50px",
        }}
      >
        <Container maxWidth="xl">
          {/* Top App Bar with Menu Icon */}
          <Grid
            container
            columnSpacing={2}
            justifyContent={"space-between"}
            wrap="nowrap"
          >
            <Grid
              display={"flex"}
              flexDirection={"column"}
              gap={"40px"}
              sx={{
                pt: 9,
                minWidth: "min(100%, 545px)",
              }}
            >
              <Paper
                elevation={0}
                sx={{ borderRadius: "10px", padding: "18px 20px" }}
              >
                <Box
                  gap={2}
                  alignItems={mediaQuery && "center"}
                  display={"flex"}
                  textAlign={"left"}
                  flexDirection={mediaQuery ? "row" : "column"}
                >
                  <Box flexGrow={1}>
                    <h3
                      style={{
                        fontWeight: "500",
                        lineHeight: 1,
                        margin: 0,
                        fontSize: 16,
                      }}
                    >
                      <Typography
                        color="brand"
                        component={"span"}
                        fontWeight={700}
                      >
                        $20 Per File
                      </Typography>{" "}
                      – Simple, Transparent Pricing
                    </h3>
                    <Box mt={0.5}>
                      <Typography fontSize={14} color="#8B8B8B">
                        No subscriptions. Just $20 per list.
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    LinkComponent={Link}
                    to={"/pricing"}
                    variant="text"
                    color="inherit"
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      lineHeight: 1.14,
                      bgcolor: "#F2F3F4",
                      border: "1px solid rgba(223, 224, 227, 1)",
                      padding: "11px 24px",
                      borderRadius: "8px",
                      "&:hover": {
                        bgcolor: "#fff",
                      },
                    }}
                  >
                    Pricing Details
                  </Button>
                </Box>
              </Paper>
              {/* File Upload Section */}
              <Box>
                <Box>
                  <Typography variant="h1">Fast and Easy Cleaning</Typography>
                  <Typography
                    variant="body4"
                    component={"p"}
                    color="#8B8B8B"
                    mt={1.5}
                    sx={{ textWrap: "balance" }}
                  >
                    Upload your Property Radar or PropStream list to get started
                  </Typography>
                </Box>

                <Box maxWidth={"476px"}>
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    sx={{
                      mt: "40px",
                    }}
                  >
                    <Step expanded>
                      <StepLabel>Select List Type:</StepLabel>
                      <StepContent>
                        <FormControl fullWidth size="small">
                          <Select
                            value={listType}
                            onChange={handleListTypeChange}
                            native
                            IconComponent={() => (
                              <ArrowDownIcon
                                style={{
                                  pointerEvents: "none",
                                  position: "absolute",
                                  right: "16px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              />
                            )}
                          >
                            <option value="" selected>
                              Select Type
                            </option>
                            <option value="property_radar">
                              Property Radar
                            </option>
                            <option value="propstream">PropStream</option>
                          </Select>
                        </FormControl>
                      </StepContent>
                    </Step>
                    <Step expanded>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <StepLabel>Upload Your File:</StepLabel>
                        <HtmlTooltip
                          placement="top"
                          arrow
                          disableFocusListener
                          enterTouchDelay={0}
                          title={
                            <Typography
                              variant="caption2"
                              component={"p"}
                              lineHeight={"16px"}
                            >
                              Files are limited to{" "}
                              <span style={{ fontWeight: "bold" }}>
                                20, 000 rows per upload
                              </span>
                              . If your file exceeds this limit, please contact
                              us at{" "}
                              <a
                                href="mailto:support@realestatelistcleaner.com"
                                style={{
                                  color: "#F69133",
                                  fontWeight: "bold",
                                  textDecoration: "none",
                                }}
                              >
                                support@realestatelistcleaner.com
                              </a>{" "}
                              for assistance.
                            </Typography>
                          }
                        >
                          <Box>
                            <InfoCircleIcon />
                          </Box>
                        </HtmlTooltip>
                      </Box>
                      <StepContent>
                        {/* ✅ Show uploaded file name */}
                        {uploadedFileName ? (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"20px"}
                            sx={{
                              border: "1px solid #D7D7D7",
                              borderRadius: "10px",
                              p: "10px 20px",
                              bgcolor: "rgba(255, 255, 255, 0.2)",
                              borderColor: isCleaning ? "#F69133" : "#D7D7D7",
                            }}
                          >
                            <CsvIcon style={{ flexShrink: 0 }} />

                            <Box
                              maxWidth={"16.25rem"}
                              flexGrow={1}
                              minWidth={0}
                            >
                              <Typography
                                fontSize={"14px"}
                                variant="body8"
                                component={"p"}
                                color="#333"
                                noWrap
                              >
                                {uploadedFileName}
                              </Typography>
                              {/* TODO: Implement on backend. */}
                              {/*
                              <Typography
                                fontSize={"14px"}
                                variant="body8"
                                component={"p"}
                                color="rgba(130, 130, 130, 0.6)"
                                mt={0.5}
                              >
                                {uploadedFileSize / 1000 / 1024}MB
                              </Typography>{" "}
                              */}
                            </Box>

                            {isCleaning ? (
                              <Box
                                color={"#F69133"}
                                sx={{
                                  "& > svg": {
                                    display: "block",
                                    animation: "rotation 1s infinite linear",
                                  },
                                  ml: "auto",
                                  "@keyframes rotation": {
                                    from: { transform: "rotate(0deg)" },
                                    to: { transform: "rotate(360deg)" },
                                  },
                                }}
                              >
                                <RefreshIcon />
                              </Box>
                            ) : (
                              <IconButton 
                                size="small"
                                color="error" 
                                sx={{
                                  ":hover": { color: "#d32f2f" }, // Changes color on hover
                                  m: "0 -5px 0 auto",
                                }}
                                onClick={handleRemoveFile} // ✅ Add this function to remove the file
                              >
                                <TrashIcon />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          <DragDropUploader
                            onUploadSuccess={handleUploadSuccess}
                            listType={listType}
                            setMessage={setMessage}
                          />
                        )}
                      </StepContent>
                    </Step>
                    <Step expanded>
                      <StepLabel>Clean File:</StepLabel>
                      <StepContent>
                        {isCleaned ? (
                          <>
                            <Box
                              display={"flex"}
                              gap={"12px"}
                              flexWrap={"wrap"}
                            >
                              {paid ? (
                                <Button
                                  variant="contained"
                                  color="brand"
                                  onClick={() =>
                                    handleDownload(cleanedFilePath)
                                  }
                                  disableElevation
                                  disabled={isCleaning}
                                  sx={{ flexGrow: 1 }}
                                >
                                  Download Cleaned File
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="default"
                                  onClick={handlePayment}
                                  disableElevation
                                  disabled={loading || isCleaning}
                                  sx={{ flexGrow: 1 }}
                                >
                                  {loading
                                    ? "Redirecting..."
                                    : "Proceed to Payment"}
                                </Button>
                              )}

                              <Button
                                variant="outlined"
                                color="brand"
                                onClick={handleDownloadSample}
                                disableElevation
                                disabled={loading || isCleaning}
                                sx={{
                                  flexGrow: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {loading ? (
                                  "Preparing Sample..."
                                ) : (
                                  <>
                                    <DownloadIcon /> Download Sample File
                                  </>
                                )}
                              </Button>
                            </Box>

                            <Paper
                              elevation={0}
                              sx={{
                                bgcolor: "#E1E2E4",
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                mt: "34px",
                                p: "14px 20px",
                                borderRadius: "10px",
                              }}
                            >
                              <Typography
                                display={"flex"}
                                alignItems={"center"}
                                gap={"10px"}
                                variant="body6"
                              >
                                <CheckCircleIcon />

                                <span>
                                  Cleaned file Rows:{" "}
                                  <span style={{ fontWeight: 600 }}>
                                    {cleanedRowCount}
                                  </span>
                                </span>
                              </Typography>

                              <Typography
                                display={"flex"}
                                alignItems={"center"}
                                gap={"10px"}
                                variant="body6"
                              >
                                <CloseCircleIcon style={{ color: "#F65633" }} />

                                <span>
                                  Removed Rows:{" "}
                                  <span style={{ fontWeight: 600 }}>
                                    {removedRowCount}
                                  </span>
                                </span>
                              </Typography>
                            </Paper>
                          </>
                        ) : (
                          <Box>
                            {isCleaning ? (
                              <Button
                                variant="contained"
                                color="default"
                                disableElevation
                                sx={{
                                  minWidth: "134px",
                                  py: "9.5px",
                                  "& > svg": {
                                    display: "block",
                                    animation: "rotation 1s infinite linear",
                                  },
                                }}
                              >
                                <RefreshIcon />
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="default"
                                onClick={handleCleanFile}
                                disableElevation
                                disabled={
                                  !uploadedFilePath || isCleaning || isCleaned
                                }
                                sx={{ minWidth: "134px" }}
                              >
                                Start
                              </Button>
                            )}
                          </Box>
                        )}

                        <Typography
                          variant="caption"
                          mt={"20px"}
                          component={"p"}
                        >
                          {message}
                        </Typography>
                      </StepContent>
                    </Step>
                  </Stepper>
                </Box>

                <Box style={{ display: "none" }}>
                  {/* ✅ Select List Type */}
                  <div style={{ marginBottom: "15px" }}>
                    <label htmlFor="listType" style={{ marginRight: "10px" }}>
                      Select List Type:
                    </label>
                    <select
                      id="listType"
                      onChange={handleListTypeChange}
                      value={listType}
                      style={{ padding: "5px" }}
                      disabled={isCleaning}
                    >
                      <option value="property_radar">Property Radar</option>
                      <option value="propstream">PropStream</option>
                    </select>
                  </div>

                  {/* ✅ Replace file input with DragDropUploader */}
                  <DragDropUploader
                    onUploadSuccess={handleUploadSuccess}
                    listType={listType}
                    setMessage={setMessage}
                  />

                  {/* ✅ Show uploaded file name */}
                  {uploadedFileName && (
                    <p
                      style={{
                        color: "white",
                        marginTop: "10px",
                        fontSize: "14px",
                      }}
                    >
                      📂 <strong>{uploadedFileName}</strong>
                    </p>
                  )}

                  {/* ✅ Clean File Button + Cleaning Message */}
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "10px",
                      minHeight: "60px",
                    }}
                  >
                    <button
                      onClick={handleCleanFile}
                      disabled={!uploadedFilePath || isCleaning || isCleaned}
                      style={{
                        marginRight: "10px",
                        opacity: isCleaning ? "0.5" : "1",
                        cursor: isCleaning ? "not-allowed" : "pointer",
                      }}
                    >
                      Clean File
                    </button>

                    {/* ✅ Show Cleaning Message & Spinner Without Pushing Button Down */}
                    <div style={{ minHeight: "30px" }}>
                      {isCleaning && (
                        <p
                          style={{
                            color: "white",
                            textAlign: "center",
                            fontSize: "16px",
                            fontWeight: "bold",
                            marginTop: "5px",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          ⚙️ Cleaning file, please wait...
                          <ClipLoader
                            color="#ffffff"
                            size={20}
                            style={{ marginLeft: "10px" }}
                          />
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ✅ Show Cleaned File Actions Only When Cleaning is Done */}
                  {isCleaned && (
                    <div style={{ marginTop: "20px", textAlign: "center" }}>
                      <button
                        onClick={handleDownloadSample}
                        disabled={loading || isCleaning}
                        style={{ marginRight: "10px" }}
                      >
                        {loading
                          ? "Preparing Sample..."
                          : "Download Sample File"}
                      </button>

                      {paid ? (
                        <button
                          onClick={() => handleDownload(cleanedFilePath)}
                          disabled={isCleaning}
                        >
                          Download Cleaned File
                        </button>
                      ) : (
                        <button
                          onClick={handlePayment}
                          disabled={loading || isCleaning}
                        >
                          {loading ? "Redirecting..." : "Proceed to Payment"}
                        </button>
                      )}

                      {/* ✅ Show Row Counts */}
                      {cleanedRowCount !== null && removedRowCount !== null && (
                        <div style={{ marginTop: "15px", color: "white" }}>
                          <p>
                            ✅ <strong>Cleaned File Rows:</strong>{" "}
                            {cleanedRowCount}
                          </p>
                          <p>
                            ❌ <strong>Removed Rows:</strong> {removedRowCount}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ✅ Display Status Messages */}
                  <p
                    className="message"
                    style={{ marginTop: "20px", color: "white" }}
                  >
                    {message}
                  </p>

                  {/* ✅ Contact Support Message */}
                  <p
                    className="support-message"
                    style={{
                      marginTop: "20px",
                      fontSize: "14px",
                      color: "#bbb",
                    }}
                  >
                    If you experience any issues, please contact
                    <a
                      href="mailto:support@realestatelistcleaner.com"
                      style={{ color: "#007bff", textDecoration: "none" }}
                    >
                      {" "}
                      support@realestatelistcleaner.com
                    </a>
                    .
                  </p>
                </Box>
              </Box>
            </Grid>

            <Grid size={6}>
              <Box sx={{ display: mediaQuery ? "block" : "none" }}>
                <img
                  src="/img-1.svg"
                  width={795}
                  height={900}
                  alt="illustration"
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  ); // ✅ Correctly closes the return statement
}; // ✅ Closes the FileUpload function

export default FileUpload;
